import { useEffect, useMemo, useState } from 'react';
import { useNoticeToast } from '../lib/toast';
import {
  api, uploadAcctDriverFile, type DriverOrderKind, type FixedRuleDraft, type FixedRuleRow, type FixedRun, type ProxyDriver,
} from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';
import Modal from '../components/Modal';

/**
 * 기사 매출 대리 처리(게시판 #206 이현숙 · 2026-10-13 way 「웹 회계 화면에서」).
 * ① 「기사 대신 신청」(세금계산서) · 「기사 대신 등록」(페이앱·카드·현금·다수배분) — 기사 앱 신청 화면과 같은 칸.
 *    서버도 기사 앱과 **같은 길**로 넣는다(번호·신청자 「이름 | 차량」·배분 줄) — 기사는 「내 내역보기」에서 자기 신청으로 본다.
 * ② 매월 고정발행 — 계산서 한 장을 규칙으로(「매월 고정으로」) · 「이번 달 만들기」 미리보기 → 만들기. 만든 계산서는 발행 전.
 */

const kst = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const digits = (s: string) => s.replace(/[^0-9]/g, '');
const won = (n: number | string) => Number(n).toLocaleString();
/** 입력 금액 → 공급가·부가세(원). 포함가면 공급가 = 합계÷1.1 반올림 · 아니면 세액 = 10% 원 미만 절사(기사 앱과 같은 규칙). */
function amounts(raw: string, included: boolean): { supply: number; vat: number } | null {
  const n = Number(digits(raw));
  if (!n) return null;
  if (included) { const supply = Math.round(n / 1.1); return { supply, vat: n - supply }; }
  return { supply: n, vat: Math.floor(n / 10) };
}
const KINDS: DriverOrderKind[] = ['페이앱', '카드단말기', '현금영수증', '다수배분'];

/** 기사 고르기 — 이름·차량번호(지금 운행 중 편성이 있는 기사). 고르면 그 기사의 법인(지금 편성)이 같이 온다. */
export function ProxyDriverPicker({ value, onPick, exclude = [], placeholder = '기사 이름 또는 차량번호' }: {
  value: ProxyDriver | null; onPick: (d: ProxyDriver | null) => void; exclude?: number[]; placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ProxyDriver[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (value || q.trim().length < 2) { setRows([]); return; }
    let live = true;
    const t = setTimeout(() => {
      setBusy(true);
      api.searchAcctDrivers({ q: q.trim() }).then((r) => { if (live) setRows(r.rows); }).catch(() => { if (live) setRows([]); })
        .finally(() => { if (live) setBusy(false); });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q, value]);
  if (value) {
    return (
      <div className="flex items-center gap-2">
        <div className="field flex-1 truncate bg-canvas">{value.label}{value.grpName && <span className="ml-2 text-xs text-ink-faint">{value.grpName}</span>}</div>
        <button type="button" className="btn-ghost shrink-0" onClick={() => { onPick(null); setQ(''); }}>변경</button>
      </div>
    );
  }
  const shown = rows.filter((r) => !exclude.includes(r.driverId));
  return (
    <div className="space-y-1">
      <input className="field w-full" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
      {shown.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded-lg border border-line text-sm">
          {shown.map((r) => (
            <li key={r.driverId}>
              <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-canvas" onClick={() => { onPick(r); setRows([]); setQ(''); }}>
                {r.label}
                <span className="ml-2 text-xs text-ink-faint">{r.grpName ?? ''}{r.corps.length ? ` · 법인 ${r.corps.map((c) => c.short_name).join('·')}` : ' · 회계 법인 편성 없음'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!busy && q.trim().length >= 2 && shown.length === 0 && <p className="text-xs text-ink-faint">운행 중인 기사 중 찾는 사람이 없습니다.</p>}
    </div>
  );
}

type Dist = { key: number; driverId?: number; type?: '외부배분-개인' | '외부배분-법인'; label: string; amount: string };

/** 기사 대신 신청 — `mode` invoice = 세금계산서 · order = 페이앱·카드단말기·현금영수증·다수배분. */
export function ProxyRequestForm({ mode, onClose, onSaved }: { mode: 'invoice' | 'order'; onClose: () => void; onSaved: (no: string, note: string) => void }) {
  const isInv = mode === 'invoice';
  const [driver, setDriver] = useState<ProxyDriver | null>(null);
  const [form, setForm] = useState<Awaited<ReturnType<typeof api.getAcctDriverForm>> | null>(null);
  const [kind, setKind] = useState<DriverOrderKind>('페이앱');
  const [corpId, setCorpId] = useState<number | null>(null);
  const [cust, setCust] = useState({ name: '', biz: '', email: '', phone: '', cashId: '' });
  const [found, setFound] = useState<{ cust_name: string; biz_number: string | null; email: string | null }[]>([]);
  const [picked, setPicked] = useState(false);
  const [requestDate, setRequestDate] = useState(kst());
  const [amount, setAmount] = useState('');
  const [included, setIncluded] = useState(false);
  const [memo, setMemo] = useState('');
  const [attach, setAttach] = useState<{ path: string; type: string; name: string } | null>(null);
  const [dists, setDists] = useState<Dist[]>([]);
  const [extName, setExtName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dasu = !isInv && kind === '다수배분';

  useEffect(() => {
    setForm(null); setCorpId(null); setAttach(null); setDists([]);
    if (!driver) return;
    api.getAcctDriverForm({ driverId: driver.driverId })
      .then((f) => { setForm(f); if (f.corps.length === 1) setCorpId(f.corps[0]!.corp_id); })
      .catch((e) => setError(errMsg(e)));
  }, [driver]);
  useEffect(() => {
    if (picked || cust.name.trim().length < 2) { setFound([]); return; }
    const t = setTimeout(() => { api.searchAcctDriverCustomers({ q: cust.name.trim() }).then((r) => setFound(r.rows)).catch(() => setFound([])); }, 300);
    return () => clearTimeout(t);
  }, [cust.name, picked]);

  const pick = (c: { cust_name: string; biz_number: string | null; email: string | null; phone?: string | null }) => {
    setCust((x) => ({ ...x, name: c.cust_name, biz: c.biz_number ?? '', email: c.email ?? '', phone: c.phone ?? x.phone }));
    setPicked(true); setFound([]);
  };
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !driver) return;
    setBusy('파일 올리는 중…'); setError(null);
    try { setAttach(await uploadAcctDriverFile(driver.driverId, f)); } catch (err) { setError(errMsg(err)); } finally { setBusy(null); }
  }
  const amt = amounts(amount, included);
  const distSum = dists.reduce((s, d) => s + Number(digits(d.amount) || 0), 0);

  async function submit() {
    setError(null);
    if (!driver) return setError('기사를 고르세요.');
    if (!corpId) return setError('법인을 고르세요.');
    if (!dasu && !cust.name.trim()) return setError('거래처를 입력하세요.');
    if (isInv && !cust.email.trim()) return setError('발행 이메일을 입력하세요.');
    if (!isInv && kind === '현금영수증' && !cust.cashId.trim()) return setError('현금영수증 발급 번호(휴대폰 또는 사업자번호)를 입력하세요.');
    if (!dasu && !amt) return setError('금액을 입력하세요.');
    if (dasu && dists.length === 0) return setError('배분 항목을 1개 이상 넣으세요.');
    if (dists.some((d) => !Number(digits(d.amount)))) return setError('배분 금액을 입력하세요.');
    if (!dasu && amt && distSum > amt.supply) return setError('배분 합계가 공급가보다 큽니다.');
    setBusy('저장하는 중…');
    try {
      const common = {
        driverId: driver.driverId, corpId, custName: cust.name.trim() || undefined, bizNumber: cust.biz.trim() || undefined,
        email: cust.email.trim() || undefined, phone: cust.phone.trim() || undefined, requestDate, memo: memo.trim() || undefined, attach,
      };
      if (isInv) {
        const r = await api.acctCreateDriverInvoice({
          ...common, supply: String(amt!.supply), vat: String(amt!.vat),
          dists: dists.map((d) => ({ driverId: d.driverId!, amount: digits(d.amount) })),
        });
        onSaved(r.invoiceNo, r.newCustomer ? ' · 새 거래처를 기사 거래처 목록에 같이 등록했습니다' : '');
      } else {
        const r = await api.acctCreateDriverOrder({
          ...common, kind, cashId: kind === '현금영수증' ? cust.cashId.trim() : undefined,
          supply: amt ? String(amt.supply) : undefined, vat: amt ? String(amt.vat) : undefined,
          dists: dists.map((d) => (d.driverId ? { driverId: d.driverId, amount: digits(d.amount) } : { type: d.type!, name: d.label, amount: digits(d.amount) })),
        });
        onSaved(r.orderNo, '');
      }
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }

  return (
    <div className="card max-h-[90vh] space-y-4 overflow-y-auto p-5 text-sm">
      <div>
        <h2 className="font-semibold">{isInv ? '기사 대신 세금계산서 신청' : '기사 대신 등록(페이앱·카드·현금영수증·다수배분)'}</h2>
        <p className="mt-1 text-xs text-ink-faint">
          기사 앱 신청과 같은 번호·신청자(「이름 | 차량」)로 들어갑니다 — 기사는 「내 내역보기」에서 자기 신청으로 봅니다. 저장자는 내 계정으로 남습니다.
          법인은 그 기사의 지금 편성 법인 중에서만 고릅니다.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2">기사
          <ProxyDriverPicker value={driver} onPick={setDriver} />
        </div>
        {driver && form && (
          <div className="space-y-1">법인
            {form.corps.length === 0 ? <p className="text-amber-700">이 기사의 지금 편성이 회계 법인 소속이 아닙니다 — 신청할 수 없습니다.</p> : (
              <select className="field w-full" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">법인 고르기</option>
                {form.corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
              </select>
            )}
          </div>
        )}
        {driver && form && <div className="space-y-1 text-ink-soft">신청자<div className="py-2 text-ink">{form.requester}</div></div>}
        {!isInv && (
          <div className="space-y-1 sm:col-span-2">종류
            <div className="flex flex-wrap gap-1">
              {KINDS.map((k) => (
                <button key={k} type="button" onClick={() => { setKind(k); setDists([]); }}
                        className={`rounded-lg border px-3 py-1 ${kind === k ? 'border-brand-500 bg-brand-50 font-medium text-brand-700' : 'border-line text-ink-soft'}`}>{k}</button>
              ))}
            </div>
          </div>
        )}
        {!dasu && (
          <div className="space-y-1 sm:col-span-2">거래처
            <input className="field w-full" placeholder="거래처 이름 두 글자 이상 — 목록에서 고르거나 그대로 적기" value={cust.name}
                   onChange={(e) => { setCust({ ...cust, name: e.target.value }); setPicked(false); }} />
            {found.length > 0 && (
              <ul className="max-h-40 overflow-y-auto rounded-lg border border-line">
                {found.map((c, i) => (
                  <li key={i}><button type="button" className="w-full px-3 py-1 text-left hover:bg-canvas" onClick={() => pick(c)}>
                    {c.cust_name}<span className="ml-2 text-xs text-ink-faint">{c.biz_number ?? ''}</span></button></li>
                ))}
              </ul>
            )}
            {form && form.recent.length > 0 && !picked && !cust.name && (
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-ink-faint">이 기사 최근 거래처:</span>
                {form.recent.slice(0, 8).map((c, i) => (
                  <button key={i} type="button" className="rounded-full border border-line px-2 text-xs" onClick={() => pick(c)}>{c.cust_name}</button>
                ))}
              </div>
            )}
            {isInv && !picked && cust.name.trim().length >= 2 && (
              <p className="text-xs text-amber-700">목록에서 고르지 않은 거래처는 저장할 때 <b>기사 거래처 목록에 같이 등록</b>됩니다(번호가 없는 곳은 비우거나 「나라장터」처럼 글자로 · 다음엔 기사도 고를 수 있습니다).</p>
            )}
          </div>
        )}
        {!dasu && <label className="space-y-1">사업자번호(선택)<input className="field w-full" value={cust.biz} onChange={(e) => setCust({ ...cust, biz: e.target.value })} /></label>}
        {!dasu && <label className="space-y-1">{isInv ? '발행 이메일' : '이메일(선택)'}<input className="field w-full" type="email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} /></label>}
        {!isInv && kind === '현금영수증' && <label className="space-y-1">현금영수증 발급 번호<input className="field w-full" value={cust.cashId} onChange={(e) => setCust({ ...cust, cashId: e.target.value })} placeholder="휴대폰 또는 사업자번호" /></label>}
        <label className="space-y-1">거래처 연락처(선택)<input className="field w-full" inputMode="tel" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} /></label>
        <label className="space-y-1">신청일<input className="field w-full" type="date" value={requestDate} onChange={(e) => setRequestDate(e.target.value)} /></label>
        {!dasu && (
          <div className="space-y-1 sm:col-span-2">금액
            <div className="flex flex-wrap items-center gap-2">
              <input className="field w-48 text-right" inputMode="numeric" placeholder="원" value={amount ? won(digits(amount)) : ''} onChange={(e) => setAmount(digits(e.target.value))} />
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={included} onChange={(e) => setIncluded(e.target.checked)} /> 부가세 포함 금액</label>
              {amt && <span className="text-ink-soft">공급가 {won(amt.supply)} + 부가세 {won(amt.vat)} = 합계 <b className="text-ink">{won(amt.supply + amt.vat)}</b>원</span>}
            </div>
            <p className="text-xs text-ink-faint">부가세 = 공급가 10%(원 미만 절사) · 포함 금액이면 공급가 = ÷1.1 반올림 — 기사 앱과 같은 규칙.</p>
          </div>
        )}
        <div className="space-y-1 sm:col-span-2">배분{dasu ? '' : '(다른 기사에게 나눠 줄 몫 · 선택)'}
          {dists.map((d, i) => (
            <div key={d.key} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate">{d.type ? `${d.type.replace('외부배분-', '외부 ')} · ` : ''}{d.label}</span>
              <input className="field w-36 text-right" inputMode="numeric" placeholder="원" value={d.amount ? won(digits(d.amount)) : ''}
                     onChange={(e) => setDists(dists.map((x, j) => (j === i ? { ...x, amount: digits(e.target.value) } : x)))} />
              <button type="button" className="btn-ghost px-2" aria-label="배분 빼기" onClick={() => setDists(dists.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          {driver && (
            <ProxyDriverPicker value={null} placeholder="배분 받을 기사 이름·차량번호" exclude={[driver.driverId, ...dists.map((d) => d.driverId ?? 0)]}
                               onPick={(p) => { if (p) setDists((xs) => [...xs, { key: Date.now(), driverId: p.driverId, label: p.label, amount: '' }]); }} />
          )}
          {dasu && (
            <div className="flex flex-wrap items-center gap-2">
              <input className="field w-56" placeholder="외부 업체·개인 이름" value={extName} onChange={(e) => setExtName(e.target.value)} />
              {(['외부배분-개인', '외부배분-법인'] as const).map((t) => (
                <button key={t} type="button" className="btn-ghost text-xs" disabled={!extName.trim()}
                        onClick={() => { setDists((xs) => [...xs, { key: Date.now(), type: t, label: extName.trim(), amount: '' }]); setExtName(''); }}>
                  + {t.replace('외부배분-', '외부 ')}</button>
              ))}
            </div>
          )}
          {!dasu && dists.length > 0 && amt && <p className="text-xs text-ink-soft">신청 기사 몫 {won(amt.supply - distSum)}원(공급가 기준)</p>}
        </div>
        <div className="space-y-1 sm:col-span-2">첨부(사업자등록증 등 · 선택)
          {attach ? (
            <p className="flex items-center gap-3"><span className="truncate">📎 {attach.name}</span>
              <button type="button" className="text-xs underline" onClick={() => setAttach(null)}>빼기</button></p>
          ) : (
            <input type="file" accept="image/*,application/pdf" disabled={!driver || busy !== null} onChange={onFile} className="block text-xs"
                   title={driver ? '사진(JPG·PNG) 또는 PDF' : '기사를 먼저 고르세요'} />
          )}
        </div>
        <label className="space-y-1 sm:col-span-2">메모(기타 입력사항)<textarea className="field min-h-[4rem] w-full" value={memo} onChange={(e) => setMemo(e.target.value)} /></label>
      </div>
      {error && <ErrorBox message={error} />}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy !== null || !driver || !corpId} onClick={() => void submit()}>{busy ?? '저장(번호 매기기)'}</button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}

// ─── 매월 고정발행 ──────────────────────────────────────────────────────────
/** 규칙 만들기(계산서 한 장에서 — `invoiceId`) · 고치기(`row`). 고칠 때는 금액·세액·배분 금액·메모·작성일·기간·이메일만. */
export function FixedRuleForm({ invoiceId, row, onClose, onSaved }: {
  invoiceId?: number; row?: FixedRuleRow; onClose: () => void; onSaved: (msg: string) => void;
}) {
  const editing = Boolean(row);
  const [draft, setDraft] = useState<FixedRuleDraft | null>(row ? {
    driverId: row.driver_id, corpId: row.corp_id, custName: row.cust_name, bizNumber: row.biz_number, email: row.email, phone: row.phone ?? '',
    supply: String(Number(row.supply)), vat: String(Number(row.vat)), memo: row.memo ?? '',
    dists: row.dists.map((d) => ({ driverId: Number(d.driverId), amount: String(Number(d.amount)) })),
    issueDay: row.issue_day, startMonth: row.start_month, endMonth: row.end_month,
  } : null);
  const [info, setInfo] = useState<{ requester: string | null; candidates: { driverId: number; label: string }[]; existing: number[] } | null>(null);
  const [driver, setDriver] = useState<ProxyDriver | null>(null);
  const [corps, setCorps] = useState<{ corp_id: number; short_name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceId || row) return;
    api.getDriverInvoiceFixedDraft({ invoiceId })
      .then((r) => { setDraft({ ...r.rule, supply: String(Number(r.rule.supply)), vat: String(Number(r.rule.vat)), dists: r.rule.dists.map((d) => ({ ...d, amount: String(Number(d.amount)) })) });
        setInfo({ requester: r.requester, candidates: r.candidates, existing: r.existing }); })
      .catch((e) => setError(errMsg(e)));
  }, [invoiceId, row]);
  // 기사의 지금 편성 법인 — 규칙 법인이 거기 없으면 경고(만들 때 그 달은 건너뛴다).
  useEffect(() => {
    if (!draft?.driverId) { setCorps([]); return; }
    api.getAcctDriverForm({ driverId: draft.driverId }).then((f) => setCorps(f.corps)).catch(() => setCorps([]));
  }, [draft?.driverId]);

  if (!draft) return <div className="card p-5 text-sm">{error ? <ErrorBox message={error} /> : '불러오는 중…'}<div className="pt-3"><button className="btn-ghost" onClick={onClose}>닫기</button></div></div>;
  const set = <K extends keyof FixedRuleDraft>(k: K, v: FixedRuleDraft[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const supplyN = Number(digits(draft.supply)) || 0;
  const vatShown = draft.vat === '' ? Math.floor(supplyN / 10) : Number(digits(draft.vat)) || 0;
  const corpOk = corps.some((c) => c.corp_id === draft.corpId);

  async function save() {
    if (!draft) return;
    setBusy(true); setError(null);
    try {
      const r = await api.saveDriverInvoiceFixed({
        id: row?.fixed_id, sourceInvoiceId: invoiceId ?? null, ...draft,
        supply: digits(draft.supply), vat: draft.vat === '' ? '' : digits(draft.vat), endMonth: draft.endMonth || null,
        dists: draft.dists.map((d) => ({ driverId: d.driverId, amount: digits(d.amount) })),
      });
      onSaved(editing ? `고정발행 #${r.id} 을 고쳤습니다.` : `고정발행 #${r.id} 을 만들었습니다 — 「고정발행」에서 달마다 만듭니다.`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="card max-h-[90vh] space-y-4 overflow-y-auto p-5 text-sm">
      <div>
        <h2 className="font-semibold">{editing ? `고정발행 #${row!.fixed_id} 고치기` : '매월 고정으로 — 이 계산서를 달마다'}</h2>
        <p className="mt-1 text-xs text-ink-faint">「고정발행 → 이번 달 만들기」를 누르면 그 달 작성일로 같은 계산서가 한 장씩 생깁니다(발행 전 — 보통 계산서처럼 청구완료). 같은 달은 두 번 안 만듭니다.</p>
      </div>
      {info && info.existing.length > 0 && <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800">이 계산서로 만든 고정발행이 이미 있습니다(#{info.existing.join(', #')}).</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2">기사
          {editing || (info?.candidates.length === 1 && !driver) ? (
            <div className="field bg-canvas">{row?.requester ?? info?.candidates[0]?.label ?? `#${draft.driverId}`}</div>
          ) : (
            <>
              {info?.requester && <p className="text-xs text-amber-700">시트에서 옮긴 계산서라 기사가 정해져 있지 않습니다(신청자 「{info.requester}」){info.candidates.length > 1 ? ` — 후보 ${info.candidates.length}명` : ''}. 기사를 고르세요.</p>}
              {info && info.candidates.length > 1 && !driver && (
                <div className="flex flex-wrap gap-1">{info.candidates.map((c) => (
                  <button key={c.driverId} type="button" className={`rounded-lg border px-2 py-0.5 text-xs ${draft.driverId === c.driverId ? 'border-brand-500 bg-brand-50' : 'border-line'}`}
                          onClick={() => set('driverId', c.driverId)}>{c.label}</button>))}</div>
              )}
              <ProxyDriverPicker value={driver} onPick={(d) => { setDriver(d); set('driverId', d?.driverId ?? null); }} />
            </>
          )}
        </div>
        <div className="space-y-1">법인
          {editing ? <div className="field bg-canvas">{row!.corp_name}</div> : (
            <select className="field w-full" value={draft.corpId} onChange={(e) => set('corpId', Number(e.target.value))}>
              {!corps.some((c) => c.corp_id === draft.corpId) && <option value={draft.corpId}>계산서 법인(#{draft.corpId})</option>}
              {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
            </select>
          )}
          {draft.driverId && corps.length > 0 && !corpOk && <p className="text-xs text-amber-700">이 법인은 기사의 지금 편성 법인({corps.map((c) => c.short_name).join('·')})이 아닙니다 — 만들 때 건너뜁니다.</p>}
        </div>
        <label className="space-y-1">거래처<input className="field w-full" value={draft.custName} disabled={editing} onChange={(e) => set('custName', e.target.value)} /></label>
        <label className="space-y-1">사업자번호<input className="field w-full" value={draft.bizNumber} disabled={editing} onChange={(e) => set('bizNumber', e.target.value)} /></label>
        <label className="space-y-1">발행 이메일<input className="field w-full" value={draft.email} onChange={(e) => set('email', e.target.value)} /></label>
        <label className="space-y-1">거래처 연락처<input className="field w-full" value={draft.phone} onChange={(e) => set('phone', e.target.value)} /></label>
        <label className="space-y-1">공급가액<input className="field w-full text-right" inputMode="numeric" value={draft.supply ? won(digits(draft.supply)) : ''} onChange={(e) => set('supply', digits(e.target.value))} /></label>
        <label className="space-y-1">세액(비우면 10%)<input className="field w-full text-right" inputMode="numeric" placeholder={won(Math.floor(supplyN / 10))}
          value={draft.vat ? won(digits(draft.vat)) : ''} onChange={(e) => set('vat', digits(e.target.value))} /></label>
        <div className="space-y-1 text-ink-soft">합계<div className="py-2 text-right text-ink">{won(supplyN + vatShown)}</div></div>
        <label className="space-y-1">작성일(매월 며칠)<input className="field w-full" type="number" min={1} max={31} value={draft.issueDay} onChange={(e) => set('issueDay', Number(e.target.value))} title="그 달에 없는 날은 말일" /></label>
        <label className="space-y-1">시작월<input className="field w-full" type="month" value={draft.startMonth} onChange={(e) => set('startMonth', e.target.value)} /></label>
        <label className="space-y-1">종료월(비우면 계속)<input className="field w-full" type="month" value={draft.endMonth ?? ''} onChange={(e) => set('endMonth', e.target.value || null)} /></label>
        {draft.dists.length > 0 && (
          <div className="space-y-1 sm:col-span-2">배분(받는 기사는 그대로 · 금액만 고칩니다)
            {draft.dists.map((d, i) => (
              <div key={d.driverId} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate">{d.label ?? `기사 #${d.driverId}`}</span>
                <input className="field w-36 text-right" inputMode="numeric" value={d.amount ? won(digits(d.amount)) : ''}
                       onChange={(e) => set('dists', draft.dists.map((x, j) => (j === i ? { ...x, amount: digits(e.target.value) } : x)))} />
                {!editing && <button type="button" className="btn-ghost px-2" onClick={() => set('dists', draft.dists.filter((_, j) => j !== i))}>×</button>}
              </div>
            ))}
          </div>
        )}
        <label className="space-y-1 sm:col-span-2">메모<input className="field w-full" value={draft.memo} onChange={(e) => set('memo', e.target.value)} /></label>
      </div>
      {error && <ErrorBox message={error} />}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy || !draft.driverId} onClick={() => void save()}>{busy ? '저장 중…' : editing ? '저장' : '고정발행 만들기'}</button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}

/** 「고정발행」 창 — 규칙 목록(고치기·끄기) · 이번 달 만들기(미리보기 → 만들기). */
export function FixedPanel({ canWrite, onClose, onChanged }: { canWrite: boolean; onClose: () => void; onChanged: () => void }) {
  const [rows, setRows] = useState<FixedRuleRow[] | null>(null);
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<FixedRuleRow | null>(null);
  const [offArm, setOffArm] = useState<number | null>(null);
  const [month, setMonth] = useState(kst().slice(0, 7));
  const [run, setRun] = useState<FixedRun | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  useEffect(() => {
    api.listDriverInvoiceFixed().then((r) => setRows(r.rows)).catch((e) => setError(errMsg(e)));
  }, [nonce]);
  const reload = () => setNonce((n) => n + 1);
  const sum = useMemo(() => (rows ?? []).reduce((s, r) => s + Number(r.supply) + Number(r.vat), 0), [rows]);

  async function preview() {
    setBusy('preview'); setError(null); setNotice(null);
    try { setRun(await api.previewDriverInvoiceFixed({ month })); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  async function commit() {
    setBusy('commit'); setError(null);
    try {
      const r = await api.commitDriverInvoiceFixed({ month });
      setRun(r);
      setNotice(`${month} — ${r.create}장 만들었습니다${r.skip ? ` · 건너뜀 ${r.skip}` : ''}${r.fail ? ` · 막힘 ${r.fail}` : ''}. 발행대장(기사개인오더)에서 보통 계산서처럼 청구완료하세요.`);
      reload(); onChanged();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  async function off(id: number) {
    if (offArm !== id) { setOffArm(id); return; }
    setOffArm(null); setError(null);
    try { await api.deleteDriverInvoiceFixed({ id }); setNotice(`고정발행 #${id} 을 껐습니다(이미 만든 계산서는 그대로).`); reload(); } catch (e) { setError(errMsg(e)); }
  }

  return (
    <div className="card max-h-[90vh] space-y-4 overflow-y-auto p-5 text-sm">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold">매월 고정발행 — 기사 세금계산서</h2>
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      <p className="text-xs text-ink-faint">
        규칙은 기사개인오더 계산서를 눌러 「매월 고정으로」에서 만듭니다. 「이번 달 만들기」 = 그 달 작성일로 한 장씩(번호는 기사 신청과 같은 순번) ·
        이미 만든 달·편성이 끝난 기사·지금 편성 법인과 안 맞는 규칙은 사유와 함께 건너뜁니다. 만든 계산서는 <b>발행 전</b>입니다.
      </p>
      {error && <ErrorBox message={error} />}
      <div className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas px-3 py-2">
        만들 달 <input type="month" className="field w-40" value={month} onChange={(e) => { setMonth(e.target.value); setRun(null); }} />
        <button className="btn" disabled={busy !== null || !month} onClick={() => void preview()}>{busy === 'preview' ? '보는 중…' : '미리보기'}</button>
        {run && !run.commit && run.month === month && (
          <button className="btn-primary" disabled={busy !== null || !canWrite || run.create === 0} onClick={() => void commit()}
                  title={canWrite ? '' : '회계 입력 권한이 필요합니다'}>{busy === 'commit' ? '만드는 중…' : `${run.create}장 만들기`}</button>
        )}
      </div>
      {run && run.month === month && (
        <div className="max-h-72 overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white"><tr className="border-b text-left text-ink-soft">
              <th className="px-2 py-1">결과</th><th>번호</th><th>기사</th><th>법인</th><th>거래처</th><th>작성일</th><th className="text-right">합계</th><th>사유</th></tr></thead>
            <tbody>
              {run.items.map((i) => (
                <tr key={i.fixedId} className={`border-b ${i.action === 'create' ? '' : 'text-ink-faint'}`}>
                  <td className="px-2 py-1">{i.action === 'create' ? (run.commit ? '만듦' : '만들 것') : i.action === 'fail' ? <span className="text-red-600">막힘</span> : '건너뜀'}</td>
                  <td className="font-mono">{dash(i.invoiceNo)}</td><td>{dash(i.requester)}</td><td>{i.corpName}</td><td className="max-w-[10rem] truncate">{i.custName}</td>
                  <td>{i.writtenDate}</td><td className="text-right">{money(Number(i.supply) + Number(i.vat))}</td>
                  <td className={i.action === 'fail' ? 'text-red-600' : 'text-amber-700'}>{i.reason ?? ''}</td>
                </tr>
              ))}
              {run.items.length === 0 && <tr><td colSpan={8} className="px-2 py-2 text-ink-faint">이 달에 해당하는 고정발행이 없습니다.</td></tr>}
            </tbody>
          </table>
          {!run.commit && <p className="px-2 py-1 text-ink-faint">미리보기 — 번호는 지금 만들면 받을 번호입니다(아직 안 넣었습니다).</p>}
        </div>
      )}
      <div className="max-h-[40vh] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white"><tr className="border-b text-left text-ink-soft">
            <th className="px-2 py-1">#</th><th>기사</th><th>법인</th><th>거래처</th><th className="text-right">공급가</th><th className="text-right">세액</th>
            <th>매월</th><th>기간</th><th>마지막 달</th><th /></tr></thead>
          <tbody>
            {rows?.map((r) => (
              <tr key={r.fixed_id} className="border-b">
                <td className="px-2 py-1">{r.fixed_id}</td><td>{r.requester ?? r.driver_name ?? `#${r.driver_id}`}</td><td>{r.corp_name}</td>
                <td className="max-w-[12rem] truncate" title={`${r.cust_name} ${r.biz_number}`}>{r.cust_name}</td>
                <td className="text-right">{money(r.supply)}</td><td className="text-right">{money(r.vat)}</td>
                <td>{r.issue_day}일</td><td>{r.start_month} ~ {r.end_month ?? '계속'}</td><td>{dash(r.last_month)}{r.made_n ? ` (${r.made_n}장)` : ''}</td>
                <td className="whitespace-nowrap text-right">
                  {canWrite && <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setEditing(r)}>고치기</button>}
                  {canWrite && <button className={`btn-ghost px-2 py-0.5 text-xs ${offArm === r.fixed_id ? 'text-red-600' : ''}`} onClick={() => void off(r.fixed_id)} onBlur={() => setOffArm(null)}>
                    {offArm === r.fixed_id ? '정말 끄기' : '끄기'}</button>}
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={10} className="px-2 py-2 text-ink-faint">아직 고정발행이 없습니다 — 기사개인오더 계산서를 눌러 「매월 고정으로」.</td></tr>}
          </tbody>
        </table>
        {rows && rows.length > 0 && <p className="pt-1 text-ink-faint">{rows.length}건 · 한 달 합계 {money(sum)}원</p>}
      </div>
      {editing && (
        <Modal width="max-w-3xl">
          <FixedRuleForm row={editing} onClose={() => setEditing(null)} onSaved={(m) => { setEditing(null); setNotice(m); reload(); }} />
        </Modal>
      )}
    </div>
  );
}
